import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Dumbbell, Trophy, Users, HelpCircle } from "lucide-react";
import { Header } from "@/components/site/Header";
import { ASSETS } from "@/lib/data";
import { useRegPrices } from "@/lib/useRegPrices";

const HUB_BG = "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/yku4owfn_ChatGPT%20Image%20Sep%201%2C%202026%2C%2003_22_19%20AM.png";

const OptionCard = ({ icon: Icon, title, meta, price, open, to, testid, index, badge, warn }) => {
  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={open ? { scale: 1.015 } : undefined}
      className={`relative overflow-hidden rounded-3xl border p-6 transition-colors sm:p-7 ${
        open
          ? "border-neon-pink/40 bg-surface/90 hover:border-neon-cyan/60 shadow-[0_0_50px_rgba(255,0,216,0.10)]"
          : "border-white/10 bg-surface/50 opacity-70"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border ${open ? "border-neon-cyan/40 bg-neon-cyan/10" : "border-white/10 bg-void/60"}`}>
            <Icon className={`h-5 w-5 ${open ? "text-neon-cyan" : "text-slate-500"}`} />
          </div>
          <div>
            <h2 className="font-display text-lg text-white sm:text-2xl">{title}</h2>
            <p className="mt-1 font-body text-xs text-slate-400">{meta}</p>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {price && <span className="font-display text-xl gradient-text">{price}</span>}
          <span className={`rounded-full px-3 py-1 font-mono-cap text-[9px] ${open ? (warn ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300") : "bg-white/10 text-slate-400"}`}>
            {open ? (badge || "Open") : "Coming Soon"}
          </span>
        </div>
      </div>
      {open && (
        <div className="mt-4 flex items-center gap-2 font-mono-cap text-[10px] text-neon-cyan">
          Register Now <ArrowRight className="h-3.5 w-3.5" />
        </div>
      )}
    </motion.div>
  );
  return open ? <Link to={to}>{inner}</Link> : <div aria-disabled="true">{inner}</div>;
};

export default function RegisterHubPage() {
  const prices = useRegPrices();
  return (
    <div className="relative min-h-screen text-white">
      <div className="pointer-events-none fixed inset-0 -z-10" data-testid="hub-bg">
        <img src={HUB_BG} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-void/70" />
      </div>
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex justify-center py-4">
            <img src={ASSETS.aboutPoster3} alt="WPC 3.0 — The Rise of Pahlawan" className="h-auto w-full max-w-md rounded-3xl border border-white/10 object-contain glow-ring" data-testid="hub-poster" />
          </div>
          <p className="font-mono-cap mb-3 mt-8 text-[11px] text-neon-cyan">Wilayah Pahlawan 3.0 · Stadium Titiwangsa, Kuala Lumpur</p>
          <h1 className="font-display text-4xl leading-[0.95] sm:text-6xl">
            REGISTER <span className="bg-gradient-to-r from-violet-400 via-cyan-300 to-green-400 bg-clip-text text-transparent">NOW</span>
          </h1>
          <p className="mt-4 font-body text-sm text-slate-400">Choose your registration category below.</p>
        </motion.div>

        <div className="mt-8 space-y-4" data-testid="register-hub">
          <OptionCard
            index={0}
            icon={Trophy}
            title="WPC 3.0 AMATEUR REGISTRATION"
            meta="26–27 November 2026 · Stadium Titiwangsa, KL · Includes championship ticket, jersey, singlet, medal & certificate · 400 slots only"
            price={`RM${prices.amateur}`}
            open
            to="/register/amateur"
            badge={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime() ? "Opens 22 Sept" : "Open"}
            warn={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime()}
            testid="reg-option-amateur"
          />
          <OptionCard
            index={1}
            icon={Users}
            title="Seminar Muay Thai (Kids)"
            meta="Below 12 y/o · Includes seminar ticket for child + 1 parent/guardian, jersey & lucky draw"
            price={`RM${prices.seminar_kids}`}
            open
            to="/register/seminar-kids"
            badge={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime() ? "Opens 22 Sept" : "Open"}
            warn={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime()}
            testid="reg-option-kids"
          />
          <OptionCard
            index={2}
            icon={Dumbbell}
            title="Seminar Muay Thai (Adult)"
            meta="Ages 12+ · Includes seminar + championship tickets, jersey, certificate & lucky draw"
            price={`RM${prices.seminar_adult}`}
            open
            to="/register/seminar-adult"
            badge={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime() ? "Opens 22 Sept" : "Open"}
            warn={Date.now() < new Date("2026-09-22T00:00:00+08:00").getTime()}
            testid="reg-option-adult"
          />
        </div>

        <p className="mt-8 flex flex-wrap items-center gap-x-2 font-body text-xs text-slate-500">
          <CalendarDays className="h-3.5 w-3.5 text-neon-cyan" /> 25–28 November 2026
          <span>·</span>
          <MapPin className="h-3.5 w-3.5 text-neon-pink" /> Stadium Titiwangsa, Kuala Lumpur
        </p>

        <div className="mt-10 flex justify-center">
          <Link
            to="/faq"
            className="flex items-center gap-2 rounded-full border border-neon-cyan/40 px-8 py-3.5 font-mono-cap text-[11px] text-neon-cyan transition-colors hover:bg-neon-cyan/10"
            data-testid="hub-faq-btn"
          >
            <HelpCircle className="h-4 w-4" /> FAQ — Frequently Asked
          </Link>
        </div>
      </main>
    </div>
  );
}
